import {useSyncExternalStore} from "react";
import type {Queries} from "tinybase/with-schemas";
import {ExternalStore} from "../utils/ExternalStore";
import {getOrCreate, RowIdsSnapshot} from "./row-ids-snapshot";

type SortArgs = [cellId: string, descending: boolean, offset: number, limit: number | undefined];

const sortedIdsCache = new WeakMap<Queries<any>, Map<string, RowIdsSnapshot<any>>>();

export const useSortedResultRowIds = <T>(
  queries: Queries<any>,
  queryId: string,
  ...sort: SortArgs
) => {
  const snapshot = getOrCreate(
    sortedIdsCache,
    queries,
    `${queryId}:${sort.join(":")}`,
    () =>
      new RowIdsSnapshot<T>(
        () => queries.getResultSortedRowIds(queryId, ...sort),
        (onChange) => {
          const listenerId = queries.addResultSortedRowIdsListener(queryId, ...sort, onChange);
          return () => {
            queries.delListener(listenerId);
          };
        }
      )
  ) as RowIdsSnapshot<T>;
  return useSyncExternalStore(snapshot.subscribe, snapshot.getSnapshot);
};

// A query's hydrated result rows, rebuilt only when the result changes: useSyncExternalStore
// would re-render forever on a new array per read. Listens for as long as the queries exist,
// which for a repo is the app's lifetime.
class ResultTableSnapshot<T> extends ExternalStore<T[]> {
  private snapshot: T[] | null = null;

  constructor(
    private queries: Queries<any>,
    private queryId: string,
    private hydrate: (row: any) => T
  ) {
    super();
    // fires once per transaction, after the result is up to date
    queries.addResultTableListener(queryId, () => {
      this.snapshot = null;
      this.notify();
    });
  }

  getSnapshot = (): T[] =>
    (this.snapshot ??= Object.values(this.queries.getResultTable(this.queryId)).map(this.hydrate));
}

const resultTableCache = new WeakMap<Queries<any>, Map<string, ResultTableSnapshot<any>>>();

// `hydrate` must be the same for every caller of a query: the first one is kept
export const useResultTable = <T>(
  queries: Queries<any>,
  queryId: string,
  hydrate: (row: any) => T
): T[] => {
  const snapshot = getOrCreate(
    resultTableCache,
    queries,
    queryId,
    () => new ResultTableSnapshot(queries, queryId, hydrate)
  ) as ResultTableSnapshot<T>;
  return useSyncExternalStore(snapshot.subscribe, snapshot.getSnapshot);
};
